import { cn } from '@invent/wl-ui-kit-next';

/**
 * The analysis graphics (Identity Guidelines V1, p.10–11).
 *
 * Precise linework built from the same geometry as the printed set, drawn on a
 * 100×100 grid and stroked with `currentColor` so they can only ever take a
 * brand color from their container. Used with restraint: one graphic per
 * surface, generous space around it.
 */
export type ResearchGraphicName =
  | 'rising-benchmarks'
  | 'assessment'
  | 'comparison'
  | 'pathways'
  | 'relationships'
  | 'perspective'
  | 'momentum'
  | 'evidence'
  | 'alignment'
  | 'flourish'
  | 'caliber'
  | 'connection';

interface ResearchGraphicProps {
  name: ResearchGraphicName;
  className?: string;
  /** Stroke width in grid units; the set reads best kept hairline. */
  strokeWidth?: number;
}

function Paths({ name }: { name: ResearchGraphicName }) {
  switch (name) {
    case 'rising-benchmarks':
      // Circles of growing radius sharing a tangent at the baseline.
      return (
        <>
          <circle cx="50" cy="82" r="14" />
          <circle cx="50" cy="72" r="24" />
          <circle cx="50" cy="62" r="34" />
          <circle cx="50" cy="52" r="44" />
        </>
      );

    case 'assessment':
      return (
        <>
          <path d="M8 50 L62 12 L62 88 Z" />
          <path d="M62 12 A38 38 0 0 1 62 88" />
          <path d="M8 50 H96" />
        </>
      );

    case 'comparison':
      return (
        <>
          <circle cx="36" cy="50" r="30" />
          <circle cx="64" cy="50" r="30" />
          <path d="M50 12 V88" />
        </>
      );

    case 'pathways':
      // Rounded pipes crossing an orthogonal grid.
      return (
        <>
          <path d="M34 6 V94" />
          <path d="M66 6 V94" />
          <path d="M6 34 H94" />
          <path d="M6 66 H94" />
          <path d="M34 34 H66 A16 16 0 0 1 66 66 H34" />
        </>
      );

    case 'relationships':
      return (
        <>
          <circle cx="38" cy="38" r="26" />
          <circle cx="62" cy="38" r="26" />
          <circle cx="38" cy="62" r="26" />
          <circle cx="62" cy="62" r="26" />
        </>
      );

    case 'perspective':
      return (
        <>
          <path d="M50 4 L96 50 L50 96 L4 50 Z" />
          <path d="M26 26 H74 V74 H26 Z" />
          <path d="M50 28 L72 50 L50 72 L28 50 Z" />
          <path d="M38 38 H62 V62 H38 Z" />
        </>
      );

    case 'momentum':
      // Rising zigzag strokes — the "N N N" motif.
      return (
        <>
          <path d="M10 88 V52 L28 88 V46" />
          <path d="M40 88 V38 L58 88 V30" />
          <path d="M70 88 V22 L88 88 V12" />
        </>
      );

    case 'evidence':
      // Stacked isometric layers.
      return (
        <>
          <path d="M50 8 L92 26 L50 44 L8 26 Z" />
          <path d="M50 30 L92 48 L50 66 L8 48 Z" />
          <path d="M50 52 L92 70 L50 88 L8 70 Z" />
        </>
      );

    case 'alignment':
      return (
        <>
          <path d="M50 10 L90 50 L50 90 L10 50 Z" />
          <path d="M50 30 L70 50 L50 70 L30 50 Z" />
          <path d="M4 50 H96" />
        </>
      );

    case 'flourish':
      // Square with four quarter-arcs meeting at the centre.
      return (
        <>
          <path d="M12 12 H88 V88 H12 Z" />
          <path d="M12 12 A38 38 0 0 1 50 50 A38 38 0 0 1 88 12" />
          <path d="M12 88 A38 38 0 0 0 50 50 A38 38 0 0 0 88 88" />
        </>
      );

    case 'caliber':
      return (
        <>
          <path d="M50 6 L94 90 H6 Z" />
          <path d="M50 44 L72 90 H28 Z" />
          <path d="M50 62 L61 90 H39 Z" />
        </>
      );

    case 'connection':
      return (
        <>
          <path d="M8 44 H56 V92 H8 Z" />
          <path d="M28 26 H76 V74 H28 Z" />
          <path d="M48 8 H96 V56 H48 Z" />
        </>
      );
  }
}

export function ResearchGraphic({ name, className, strokeWidth = 1 }: ResearchGraphicProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
      aria-hidden
      focusable="false"
      className={cn('pointer-events-none', className)}
    >
      <Paths name={name} />
    </svg>
  );
}
